/**
 * Authentication API Service
 * Handles all authentication-related API calls
 */

import apiClient from './axios';
import { ApiResponse, AuthResponse, LoginCredentials, RegisterData, User } from './types';

export const authApi = {
  /**
   * Register a new user (recaptcha_token required when reCAPTCHA is configured for non-logged-in forms)
   */
  register: async (data: RegisterData & { recaptcha_token?: string }): Promise<ApiResponse<AuthResponse>> => {
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/register', data);
    return response.data;
  },

  /**
   * Login with email/password or OTP
   */
  login: async (credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> => {
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/login', credentials);
    return response.data;
  },

  /**
   * Send OTP to email or phone
   */
  sendOtp: async (identifier: string | { email: string; phone: string }, type: 'email' | 'phone' | 'both' = 'email'): Promise<ApiResponse> => {
    let payload: any;
    
    if (type === 'both' && typeof identifier === 'object') {
      payload = {
        email: identifier.email,
        phone: identifier.phone,
      };
    } else {
      payload = {
        [type]: identifier,
      };
    }
    
    const response = await apiClient.post<ApiResponse>('/send-otp', payload);
    return response.data;
  },

  /**
   * Verify OTP
   */
  verifyOtp: async (identifier: string, otp: string, type: 'email' | 'phone'): Promise<ApiResponse<AuthResponse>> => {
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/verify-otp', {
      [type]: identifier,
      otp,
    });
    return response.data;
  },

  /**
   * Logout current user
   */
  logout: async (): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/logout');
    return response.data;
  },

  /**
   * Get current authenticated user
   */
  getCurrentUser: async (): Promise<ApiResponse<{ user: User }>> => {
    const response = await apiClient.get<ApiResponse<{ user: User }>>('/user');
    return response.data;
  },

  /**
   * Request password reset (recaptcha_token required when reCAPTCHA is configured)
   */
  requestPasswordReset: async (email: string, recaptchaToken?: string): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/forgot-password', {
      email,
      ...(recaptchaToken && { recaptcha_token: recaptchaToken }),
    });
    return response.data;
  },

  /**
   * Reset password with token (recaptcha_token required when reCAPTCHA is configured)
   */
  resetPassword: async (data: {
    email: string;
    token: string;
    password: string;
    password_confirmation: string;
    recaptcha_token?: string;
  }): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/reset-password', data);
    return response.data;
  },
};

